import React, { useState, useEffect } from 'react';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Users,
  Wallet,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Activity,
  UserPlus,
  RefreshCw,
  Calendar,
  MoreVertical,
  CheckCircle2,
  Clock,
  XCircle,
  Sparkles
} from 'lucide-react';
import api from '../utils/axios';
import { formatCurrency } from '../helpers';

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const getDashboardData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/getDashboardData');
      const data = response.data;
      setDashboardData(data?.data ?? {});
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        {/* Header Skeleton */}
        <div className="h-16 bg-white border border-gray-200 rounded-xl" />
        
        {/* Wallet Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="h-36 bg-white border border-gray-200 rounded-xl" />
          <div className="h-36 bg-white border border-gray-200 rounded-xl" />
        </div>

        {/* Stats Grid Skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-36 bg-white border border-gray-200 rounded-xl" />
          ))}
        </div>

        {/* Overview Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="h-56 bg-white border border-gray-200 rounded-xl" />
          <div className="h-56 bg-white border border-gray-200 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
            Admin Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Real-time overview of platform metrics and performance
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs md:text-sm text-gray-600 bg-white border border-gray-200 px-3.5 py-2 rounded-lg shadow-sm">
            <Calendar size={15} className="text-gray-400" />
            <span>Updated: {lastUpdated.toLocaleTimeString()}</span>
          </div>
          <button
            onClick={getDashboardData}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg shadow-sm transition-colors"
          >
            <RefreshCw size={15} />
            Refresh
          </button>
        </div>
      </div>

      {/* Wallet Balances Section (Preline Clean Cards) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <BalanceCard
          title="Total Client Wallet Balance"
          value={formatCurrency(dashboardData?.totalClientWalletBalance)}
          change="+20.1% from last month"
          changeType="positive"
          iconColor="text-emerald-600"
          iconBg="bg-emerald-50"
          icon={<Wallet size={22} />}
        />
        <BalanceCard
          title="Total Claimed by Agents"
          value={formatCurrency(dashboardData?.totalAgentWalletBalance)}
          change="+15% from last month"
          changeType="positive"
          iconColor="text-blue-600"
          iconBg="bg-blue-50"
          icon={<Wallet size={22} />}
        />
      </div>

      {/* Main 4 Metric Cards (Matching Preline Style) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Users"
          value={dashboardData?.totalUsers?.toLocaleString() ?? '0'}
          subtitle="+12% from last month"
          icon={<Users size={20} />}
          iconColor="text-purple-600"
          iconBg="bg-purple-50"
          trend="up"
          avatarLetters={['L', 'A']}
        />

        <MetricCard
          title="Total Deposits"
          value={formatCurrency(dashboardData?.totalDeposits)}
          subtitle="+20.1% from last month"
          icon={<ArrowUpCircle size={20} />}
          iconColor="text-blue-600"
          iconBg="bg-blue-50"
          trend="up"
          avatarLetters={['R', 'O']}
        />

        <MetricCard
          title="Total Withdrawals"
          value={formatCurrency(dashboardData?.totalWithdrawals)}
          subtitle="-5% from last month"
          icon={<ArrowDownCircle size={20} />}
          iconColor="text-rose-600"
          iconBg="bg-rose-50"
          trend="down"
          avatarLetters={['M', 'S']}
        />

        <MetricCard
          title="Net Revenue"
          value={formatCurrency(
            (dashboardData?.totalDeposits ?? 0) - 
            (dashboardData?.totalWithdrawals ?? 0)
          )}
          subtitle="Deposits - Withdrawals"
          icon={<TrendingUp size={20} />}
          iconColor="text-amber-600"
          iconBg="bg-amber-50"
          trend="up"
          avatarLetters={['C', 'F']}
        />
      </div>

      {/* Transaction Status Overview (Preline Card Panels) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <StatusCard
          title="Deposits Overview"
          badgeColor="bg-emerald-50 text-emerald-700 border-emerald-200"
          icon={<ArrowUpCircle size={18} className="text-emerald-600" />}
          stats={[
            {
              label: 'Approved',
              count: dashboardData?.deposits?.approvedCount,
              amount: dashboardData?.deposits?.approvedAmount,
              statusType: 'success',
            },
            {
              label: 'Pending',
              count: dashboardData?.deposits?.pendingCount,
              amount: dashboardData?.deposits?.pendingAmount,
              statusType: 'pending',
            },
            {
              label: 'Rejected',
              count: dashboardData?.deposits?.rejectedCount,
              amount: dashboardData?.deposits?.rejectedAmount,
              statusType: 'danger',
            },
          ]}
        />

        <StatusCard
          title="Withdrawals Overview"
          badgeColor="bg-rose-50 text-rose-700 border-rose-200"
          icon={<ArrowDownCircle size={18} className="text-rose-600" />}
          stats={[
            {
              label: 'Approved',
              count: dashboardData?.withdrawals?.approvedCount,
              amount: dashboardData?.withdrawals?.approvedAmount,
              statusType: 'success',
            },
            {
              label: 'Pending',
              count: dashboardData?.withdrawals?.pendingCount,
              amount: dashboardData?.withdrawals?.pendingAmount,
              statusType: 'pending',
            },
            {
              label: 'Rejected',
              count: dashboardData?.withdrawals?.rejectedCount,
              amount: dashboardData?.withdrawals?.rejectedAmount,
              statusType: 'danger',
            },
          ]}
        />
      </div>

      {/* Today's Activity Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <Activity size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Today's Activity</h2>
              <p className="text-xs text-gray-500">Real-time metrics calculated for today</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
            <Sparkles size={12} /> Live
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <TodayStatCard
            title="Today's Deposits"
            value={formatCurrency(dashboardData?.todayDeposits)}
            icon={<ArrowUpCircle size={18} />}
            iconColor="text-emerald-600"
            iconBg="bg-emerald-50"
          />

          <TodayStatCard
            title="Today's Withdrawals"
            value={formatCurrency(dashboardData?.todayWithdrawals)}
            icon={<ArrowDownCircle size={18} />}
            iconColor="text-rose-600"
            iconBg="bg-rose-50"
          />

          <TodayStatCard
            title="New Users Today"
            value={dashboardData?.todayUsers ?? 0}
            icon={<UserPlus size={18} />}
            iconColor="text-blue-600"
            iconBg="bg-blue-50"
          />

          <TodayStatCard
            title="Today's Net"
            value={formatCurrency(
              (dashboardData?.todayDeposits ?? 0) - 
              (dashboardData?.todayWithdrawals ?? 0)
            )}
            icon={<DollarSign size={18} />}
            iconColor="text-purple-600"
            iconBg="bg-purple-50"
          />
        </div>
      </div>

      {/* Quick Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <SummaryCard
          title="Avg. Deposit per User"
          value={formatCurrency(
            (dashboardData?.totalDeposits ?? 0) / 
            (dashboardData?.totalUsers || 1)
          )}
          icon={<TrendingUp size={20} className="text-emerald-600" />}
          iconBg="bg-emerald-50"
        />
        <SummaryCard
          title="Avg. Withdrawal per User"
          value={formatCurrency(
            (dashboardData?.totalWithdrawals ?? 0) / 
            (dashboardData?.totalUsers || 1)
          )}
          icon={<TrendingDown size={20} className="text-rose-600" />}
          iconBg="bg-rose-50"
        />
        <SummaryCard
          title="Total Platform Balance"
          value={formatCurrency(
            (dashboardData?.totalClientWalletBalance ?? 0) + 
            (dashboardData?.totalAgentWalletBalance ?? 0)
          )}
          icon={<Wallet size={20} className="text-blue-600" />}
          iconBg="bg-blue-50"
        />
      </div>
    </div>
  );
};

/* ================= COMPONENTS (PRELINE DESIGN SYSTEM) ================= */

const BalanceCard = ({
  title,
  value,
  change,
  changeType,
  iconColor,
  iconBg,
  icon,
}: {
  title: string;
  value: string;
  change: string;
  changeType: 'positive' | 'negative';
  iconColor: string;
  iconBg: string;
  icon: React.ReactNode;
}) => (
  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-lg ${iconBg} ${iconColor} flex items-center justify-center`}>
          {icon}
        </div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
      </div>
      <button className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors">
        <MoreVertical size={16} />
      </button>
    </div>

    <div className="mt-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
      <p className="text-3xl font-bold text-gray-900 tracking-tight">{value}</p>
      <div
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
          changeType === 'positive'
            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200/60'
            : 'bg-rose-50 text-rose-700 border border-rose-200/60'
        }`}
      >
        {changeType === 'positive' ? (
          <TrendingUp size={13} className="text-emerald-600" />
        ) : (
          <TrendingDown size={13} className="text-rose-600" />
        )}
        <span>{change}</span>
      </div>
    </div>
  </div>
);

const MetricCard = ({
  title,
  value,
  subtitle,
  icon,
  iconColor,
  iconBg,
  trend,
  avatarLetters,
}: {
  title: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  iconColor: string;
  iconBg: string;
  trend?: 'up' | 'down';
  avatarLetters?: string[];
}) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between">
    <div className="flex items-start justify-between">
      <div className={`w-10 h-10 rounded-lg ${iconBg} ${iconColor} flex items-center justify-center`}>
        {icon}
      </div>
      <button className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors">
        <MoreVertical size={16} />
      </button>
    </div>

    <div className="mt-4">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{title}</p>
      <div className="flex items-end justify-between mt-1">
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {avatarLetters && avatarLetters.length > 0 && (
          <div className="flex -space-x-1.5 overflow-hidden">
            {avatarLetters.map((letter, i) => (
              <span
                key={i}
                className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 text-[10px] font-semibold text-slate-700 ring-2 ring-white"
              >
                {letter}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5 mt-2">
        {trend && (
          <span
            className={`inline-flex items-center gap-1 text-xs font-medium ${
              trend === 'up' ? 'text-emerald-600' : 'text-rose-600'
            }`}
          >
            {trend === 'up' ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
          </span>
        )}
        <p className="text-xs text-gray-500">{subtitle}</p>
      </div>
    </div>
  </div>
);

const TodayStatCard = ({
  title,
  value,
  icon,
  iconColor,
  iconBg,
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  iconColor: string;
  iconBg: string;
}) => (
  <div className="bg-gray-50/70 rounded-xl p-4 border border-gray-200/80">
    <div className="flex items-center gap-2.5 mb-2">
      <div className={`w-8 h-8 rounded-lg ${iconBg} ${iconColor} flex items-center justify-center`}>
        {icon}
      </div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{title}</p>
    </div>
    <p className="text-xl font-bold text-gray-900 mt-1">{value}</p>
  </div>
);

const SummaryCard = ({
  title,
  value,
  icon,
  iconBg,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
  iconBg: string;
}) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-center gap-3 mb-2">
      <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center`}>
        {icon}
      </div>
      <p className="text-sm font-medium text-gray-600">{title}</p>
    </div>
    <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
  </div>
);

const StatusCard = ({
  title,
  badgeColor,
  icon,
  stats,
}: {
  title: string;
  badgeColor: string;
  icon: React.ReactNode;
  stats: {
    label: string;
    count: number;
    amount: number;
    statusType: 'success' | 'pending' | 'danger';
  }[];
}) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
    <div className="p-5 border-b border-gray-200 flex items-center justify-between bg-white">
      <div className="flex items-center gap-2.5">
        {icon}
        <h3 className="text-base font-bold text-gray-900">{title}</h3>
      </div>
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${badgeColor}`}>
        Overview
      </span>
    </div>

    <div className="p-5 divide-y divide-gray-100">
      {stats.map((stat) => (
        <div key={stat.label} className="py-3 first:pt-0 last:pb-0 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span
              className={`w-2.5 h-2.5 rounded-full ${
                stat.statusType === 'success'
                  ? 'bg-emerald-500 ring-4 ring-emerald-50'
                  : stat.statusType === 'pending'
                  ? 'bg-amber-500 ring-4 ring-amber-50'
                  : 'bg-rose-500 ring-4 ring-rose-50'
              }`}
            />
            <div>
              <p className="text-sm font-medium text-gray-800">{stat.label}</p>
              <p className="text-xs text-gray-400">{stat.count || 0} transactions</p>
            </div>
          </div>
          <p className="text-sm font-bold text-gray-900 font-mono">
            {formatCurrency(stat.amount || 0)}
          </p>
        </div>
      ))}
    </div>
  </div>
);

export default AdminDashboard;